/* ============================================================
   PALAMANO — Selector de pack en la ficha de producto
   Marcado en snippets/palamano-packs.liquid

   El pack es otro producto, no otra variante. Así que al elegirlo
   lo único que hace falta es mandar su id al carrito en vez del de
   la variante suelta, y enseñar su precio arriba.
   Modo «por cantidad» (nachos y galletas): la pastilla lleva
   data-cantidad y lo que cambia al enviar es la cantidad, no el id;
   el descuento lo aplica Shopify.
   ============================================================ */
(() => {
  class PalamanoPacks extends HTMLElement {
    connectedCallback() {
      this.info = this.closest('.product-info');
      this.form = this.info?.querySelector('form[action*="/cart/add"]');
      this.campoId = this.form?.querySelector('input[name="id"]');
      this.precio = this.info?.querySelector('price-list');
      if (!this.form || !this.campoId) return;

      /* En celular el botón de comprar es el que va fijo abajo, y ese tiene
         su propio formulario fuera de esta columna: hay que cubrirlo también
         o el pack elegido no viajaría al carrito. */
      this.formularios = [...document.querySelectorAll('form[action*="/cart/add"]')].filter(
        (f) => f.querySelector('input[name="id"]')
      );

      /* El precio de partida, para poder volver a él. */
      this.precioInicial = this.precio?.outerHTML ?? null;

      this.addEventListener('click', this.alElegir.bind(this));

      /* El selector de tamaño del tema reescribe el id: cada vez que
         cambia hay que enseñar el grupo de pastillas que le toca. */
      this.form.addEventListener('change', () => requestAnimationFrame(() => this.sincronizar()));
      document.addEventListener('variant:change', () => requestAnimationFrame(() => this.sincronizar()));

      /* El envío se intercepta en captura, antes que el del tema: si hay
         un pack elegido, el id que viaja es el suyo. */
      this.formularios.forEach((f) => f.addEventListener('submit', this.alEnviar.bind(this), true));

      this.sincronizar();
      this.hidden = false;
    }

    get grupoActivo() {
      return this.querySelector(`.pmn-packs__grupo[data-para="${this.campoId.value}"]`);
    }

    /* Enseña las pastillas que le tocan a la variante elegida, conservando
       si se estaba en «suelta» o en «pack». Importa en celular: al pulsar
       comprar se abre el panel del tamaño, y elegir ahí dispara esto — si
       aquí se volviera a «1 paq.» se perdería el pack recién elegido. */
    sincronizar() {
      this.querySelectorAll('.pmn-packs__grupo').forEach((g) => {
        g.hidden = g !== this.grupoActivo;
      });

      const grupo = this.grupoActivo;
      this.hidden = !grupo;
      if (!grupo) {
        this.idElegido = null;
        return;
      }

      const opciones = [...grupo.querySelectorAll('.pmn-packs__op')];
      this.marcar(opciones[this.indiceElegido] ?? opciones[0]);
    }

    alElegir(evento) {
      const op = evento.target.closest('.pmn-packs__op');
      if (op) this.marcar(op);
    }

    marcar(op) {
      if (!op) return;

      op.parentElement.querySelectorAll('.pmn-packs__op').forEach((otra) => {
        const activa = otra === op;
        otra.classList.toggle('is-active', activa);
        otra.setAttribute('aria-pressed', activa ? 'true' : 'false');
      });

      this.indiceElegido = [...op.parentElement.children].indexOf(op);

      /* Si es la pastilla suelta, el id es el de la propia variante y no
         hay nada que sustituir al enviar. */
      this.idElegido = op.dataset.id === this.campoId.value ? null : op.dataset.id;
      this.cantidadElegida = Number(op.dataset.cantidad) || null;

      const plantilla = op.querySelector('template[data-precio]');
      if (plantilla && this.precio) {
        this.precio.outerHTML = plantilla.innerHTML.trim();
        this.precio = this.closest('.product-info').querySelector('price-list');
      }
    }

    alEnviar(evento) {
      const form = evento.currentTarget;

      /* En la página hay otros formularios de añadir (recomendados, «Explora
         la colección», añadir rápido): solo se toca el que lleva la variante
         de esta ficha. Antes se tocaban todos y, con un pack elegido,
         añadir un recomendado metía el pack. */
      if (form.querySelector('input[name="id"]')?.value !== this.campoId.value) return;

      if (this.cantidadElegida) {
        /* El formulario fijo de celular no trae campo de cantidad: si falta,
           se crea uno temporal. */
        let campoCant = form.querySelector('input[name="quantity"]');
        const creado = !campoCant;
        if (creado) {
          campoCant = document.createElement('input');
          campoCant.type = 'hidden';
          campoCant.name = 'quantity';
          form.append(campoCant);
        }
        const antes = campoCant.value;
        campoCant.value = this.cantidadElegida;
        setTimeout(() => {
          if (creado) campoCant.remove();
          else campoCant.value = antes;
        });
      }

      if (!this.idElegido) return;

      const campo = form.querySelector('input[name="id"]');
      if (!campo) return;

      const suelta = campo.value;
      campo.value = this.idElegido;

      /* Se devuelve en cuanto el tema ha leído el formulario, para que el
         selector de tamaño siga sabiendo en qué variante está. */
      setTimeout(() => {
        campo.value = suelta;
      });
    }
  }

  if (!window.customElements.get('palamano-packs')) {
    window.customElements.define('palamano-packs', PalamanoPacks);
  }
})();
